import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../store';
import { requestNavigateMainTab } from '../../store/slices/modalsSlice';
import { getBillingMe } from '../../services/api';
import { daysLeft } from '../../lib/billing/trialCountdown';

/**
 * TrialCountdownBadge (PR-C3 - Billing #2 Conversion, Decision D5: in-app only).
 *
 * A small, always-mounted badge that nudges trialing users toward upgrading.
 * Visibility contract (HAZARDS H1/H3):
 *   - Shown ONLY when `isTrialing && !isComped`.
 *   - `isTrialing` is NEVER collapsed with `isPaid` - a paid subscriber sees nothing.
 *   - Comped users (complimentary access) never see it.
 *
 * Escalation by days remaining:
 *   - default  → soft badge "Trial: {{days}} days left"
 *   - days<=2  → stronger banner ("ending soon", styled after the PricingPage
 *                trialing banner)
 *   - days===0 → strongest banner ("ends today")
 *
 * Clicking navigates to the in-app Billing tab via `requestNavigateMainTab`
 * (App.tsx observes this and switches the active tab - equivalent to ?tab=billing).
 *
 * Trial end source: `billingSlice.trialEndsAt` from `fetchBillingMe` (the
 * auto-trial anchor). Falls back to legacy `subscription.trialEnd` when set,
 * else a one-shot `getBillingMe()` fetch while trialing. Test/host callers
 * may override `isTrialing`/`isComped`/`trialEnd`/`now`/`onNavigate` directly.
 */
export interface TrialCountdownBadgeProps {
  /** Override the trialing flag (defaults to billing slice). */
  isTrialing?: boolean;
  /** Override the comped flag (defaults to billing slice). */
  isComped?: boolean;
  /** Override the trial end (ISO string / Date / epoch ms). Skips the fetch. */
  trialEnd?: Date | number | string | null;
  /** Override "now" for deterministic rendering in tests. */
  now?: Date | number;
  /** Override navigation (defaults to dispatching requestNavigateMainTab('billing')). */
  onNavigate?: () => void;
  className?: string;
}

const TrialCountdownBadge: React.FC<TrialCountdownBadgeProps> = ({
  isTrialing: isTrialingProp,
  isComped: isCompedProp,
  trialEnd: trialEndProp,
  now: nowProp,
  onNavigate,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const sliceIsTrialing = useAppSelector((s) => s.billing.isTrialing);
  const sliceIsComped = useAppSelector((s) => s.billing.isComped);
  const sliceTrialEndsAt = useAppSelector((s) => s.billing.trialEndsAt);
  const sliceTrialEnd = useAppSelector(
    (s) => s.billing.subscription?.trialEnd ?? null
  );

  const isTrialing = isTrialingProp ?? sliceIsTrialing;
  const isComped = isCompedProp ?? sliceIsComped;

  // Resolve trial end: explicit prop > billing.me anchor > legacy subscription.
  const [fetchedEnd, setFetchedEnd] = useState<string | null>(null);
  const resolvedEnd =
    trialEndProp !== undefined
      ? trialEndProp
      : sliceTrialEndsAt ?? sliceTrialEnd ?? fetchedEnd;

  const shouldFetch =
    trialEndProp === undefined &&
    isTrialing &&
    !isComped &&
    !sliceTrialEndsAt &&
    !sliceTrialEnd &&
    fetchedEnd === null;

  useEffect(() => {
    if (!shouldFetch) return;
    let cancelled = false;
    getBillingMe()
      .then((me) => {
        if (!cancelled) setFetchedEnd(me.trial_ends_at);
      })
      .catch(() => {
        /* Non-critical: badge just stays hidden if we can't resolve the end. */
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetch]);

  // H1/H3: only trialing, never comped, never collapsed with paid.
  if (!isTrialing || isComped) return null;

  const now = nowProp ?? Date.now();
  const days = daysLeft(now, resolvedEnd ?? null);
  if (days === null) return null;

  const handleClick = () => {
    if (onNavigate) {
      onNavigate();
      return;
    }
    dispatch(requestNavigateMainTab('billing'));
  };

  // Escalation tone - strongest at 0, strong at <=2, soft otherwise.
  let tone: string;
  let label: string;
  if (days === 0) {
    tone =
      'bg-[color:var(--accent-bearish-muted)] text-[color:var(--accent-bearish)] border border-[color:var(--accent-bearish)] font-semibold';
    label = t('trial.endsToday', 'Trial ends today - upgrade now');
  } else if (days <= 2) {
    tone =
      'bg-[color:var(--accent-bullish-muted)] text-[color:var(--accent-bullish)] border border-[color:var(--accent-bullish)] font-semibold';
    label = t('trial.endingSoon', 'Trial ending soon: {{days}} days left', {
      days,
    });
  } else {
    tone =
      'bg-surface-container-highest text-text-secondary border border-border-subtle';
    label = t('trial.daysLeft', 'Trial: {{days}} days left', { days });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="trial-countdown-badge"
      className={[
        'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs',
        'transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent-primary',
        tone,
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={label}
    >
      <i className="fas fa-clock" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
};

export default TrialCountdownBadge;
